/**
 * QuickTaskFAB - Botón flotante para agregar tareas rápidas
 *
 * Diseño:
 * - Botón flotante en esquina inferior derecha
 * - Al click, abre modal con input y selector de bloque horario
 * - Solo visible en la vista diaria
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Sunrise, Sun, Moon } from 'lucide-react';
import { cn } from '@/core/utils';
import { useQuickTaskStore } from '@/store';
import { feedbackSuccess } from '@/services/feedback';
import type { TimeBlock } from '@/core/types';

interface QuickTaskFABProps {
  currentTimeBlock: TimeBlock;
}

const timeBlockOptions: { value: TimeBlock; label: string; icon: typeof Sunrise }[] = [
  { value: 'morning', label: 'Mañana', icon: Sunrise },
  { value: 'afternoon', label: 'Tarde', icon: Sun },
  { value: 'evening', label: 'Noche', icon: Moon },
];

export function QuickTaskFAB({ currentTimeBlock }: QuickTaskFABProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const [selectedBlock, setSelectedBlock] = useState<TimeBlock>(currentTimeBlock);
  const inputRef = useRef<HTMLInputElement>(null);
  const addQuickTask = useQuickTaskStore((state) => state.addQuickTask);

  // Actualizar bloque seleccionado cuando cambia el bloque actual
  useEffect(() => {
    if (!isOpen) {
      setSelectedBlock(currentTimeBlock);
    }
  }, [currentTimeBlock, isOpen]);

  // Focus input cuando se abre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Pequeño delay para que la animación termine
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;

    addQuickTask(trimmed, selectedBlock);
    setValue('');
    feedbackSuccess();
    setIsOpen(false);
  }, [value, selectedBlock, addQuickTask]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setValue('');
      }
    },
    [handleSubmit]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setValue('');
  }, []);

  return (
    <>
      {/* Overlay cuando está abierto */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={handleClose}
          />
        )}
      </AnimatePresence>

      {/* Modal de tarea rápida */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'fixed bottom-24 right-4 left-4 z-50',
              'sm:left-auto sm:w-80',
              'bg-slate-900 border border-slate-700 rounded-xl',
              'shadow-xl shadow-black/30'
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h3 className="text-sm font-medium text-slate-200">Tarea rápida</h3>
              <button
                onClick={handleClose}
                className="p-1 text-slate-500 hover:text-slate-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Input */}
              <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="¿Qué necesitas hacer?"
                className={cn(
                  'w-full px-3 py-2 rounded-lg',
                  'bg-slate-800 border border-slate-700',
                  'text-sm text-slate-200 placeholder:text-slate-500',
                  'focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20'
                )}
                maxLength={100}
              />

              {/* Selector de bloque */}
              <div className="space-y-2">
                <p className="text-xs text-slate-500">¿Cuándo?</p>
                <div className="flex gap-2">
                  {timeBlockOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = selectedBlock === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setSelectedBlock(option.value)}
                        className={cn(
                          'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg',
                          'text-xs font-medium transition-all',
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-300'
                        )}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Botón agregar */}
              <button
                onClick={handleSubmit}
                disabled={value.trim().length === 0}
                className={cn(
                  'w-full py-2.5 rounded-lg',
                  'text-sm font-medium transition-all',
                  value.trim().length > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-500'
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                )}
              >
                Agregar tarea
              </button>

              {/* Nota */}
              <p className="text-2xs text-slate-600 text-center">
                Las tareas rápidas desaparecen al día siguiente
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-20 right-4 z-[100]',
          'w-14 h-14 rounded-full',
          'bg-blue-600 hover:bg-blue-500',
          'text-white shadow-lg shadow-blue-900/30',
          'flex items-center justify-center',
          'transition-colors'
        )}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={isOpen ? { rotate: 45 } : { rotate: 0 }}
        title="Agregar tarea rápida"
      >
        <Plus className="w-6 h-6" />
      </motion.button>
    </>
  );
}
